import * as React from 'react';

import {BoxRegularIcon} from 'shells/icon/svg/box-regular-icon';
import {TwoPaneModalForm} from 'shells/modal/two-pane-modal-form';
import {Body} from 'shells/typography/body';

import type {ProductType} from 'nutshell-graphql-types';
import type {
    GetMarkets_markets_edges_node as Market,
    GetProducts_products_edges_node as Product,
    QuoteTerm as ProductTerm,
    ProductCreateInput,
    ProductUpdateInput,
    GetProductsCategories_productCategories as ProductCategory,
} from 'nutshell-graphql-types';

import {getFooterTimestamp} from '../../../activity-modal/helpers';

import {DEFAULT_PRODUCT_TYPE} from './consts';
import {isValidProduct} from './helpers';
import {ProductInputFields} from './product-input-fields';
import {ProductMarketFieldArray} from './product-market-field-array';
import type {MarketFieldInput} from './types';

type BaseProps = {
    onClose: () => void;
    isOpen: boolean;
    isSaving: boolean;
};

type CreateProps = BaseProps & {
    variant: 'create';
    markets: Market[];
    onProductSave: (input: ProductCreateInput) => Promise<any>;
    defaultCategory?: ProductCategory | null | undefined;
};

type UpdateProps = BaseProps & {
    variant: 'update';
    product: Product;
    onProductSave: (input: ProductUpdateInput) => Promise<any>;
};

type Props = CreateProps | UpdateProps;

export function ProductModal(props: Props) {
    // @ts-expect-error
    const {variant, markets = undefined, product = undefined} = props;

    const [productName, setProductName] = React.useState<string>('');
    const [productType, setProductType] = React.useState<ProductType>(DEFAULT_PRODUCT_TYPE);
    const [productCategory, setProductCategory] = React.useState<
        ProductCategory | null | undefined
        // @ts-expect-error
    >(props.defaultCategory || null);
    const [productUnit, setProductUnit] = React.useState<ProductTerm | null>(null);
    const [productSku, setProductSku] = React.useState<string>('');
    const [productDescription, setProductDescription] = React.useState<string>('');
    const [marketFields, setMarketFields] = React.useState<MarketFieldInput[]>([]);

    React.useEffect(() => {
        let newMarketFields = [];

        if (variant === 'create' && markets) {
            // @ts-expect-error
            newMarketFields = markets.map((market) => ({
                market,
                price: 0,
                isAvailable: true,
            }));
        } else if (variant === 'update' && product) {
            setProductName(product.name);
            setProductCategory(product.category);
            setProductUnit(product.term || null);
            setProductType(product.type);
            setProductSku(product.sku);
            setProductDescription(product.description || '');

            // @ts-expect-error
            newMarketFields = product.markets.edges.map((marketEdge) => ({
                market: marketEdge.node,
                price: marketEdge.price ? marketEdge.price.amount : 0,
                isAvailable: Boolean(marketEdge.isAvailable),
            }));
        }

        setMarketFields(newMarketFields);
    }, [variant, markets, product]);

    const handleMarketFieldChange = (marketId: string, price: number, isAvailable: boolean) => {
        setMarketFields(
            marketFields.map((field) => {
                if (field.market.id === marketId) {
                    return {...field, price, isAvailable};
                }

                return field;
            })
        );
    };

    const handleSubmit = () => {
        const unit = productType === 'SERVICE' ? productUnit : null;
        const baseInput = {
            productData: {
                name: productName,
                description: productDescription,
                type: productType,
                unit,
                sku: productSku,
                categoryId: productCategory ? productCategory.id : null,
                marketPrices: marketFields.map((marketField) => ({
                    marketId: marketField.market.id,
                    price: marketField.price,
                    available: marketField.isAvailable,
                })),
            },
        } as const;

        if (props.variant === 'update') {
            props.onProductSave({...baseInput, id: props.product.id}).then(() => {
                props.onClose();
            });
        } else {
            props.onProductSave(baseInput).then(() => {
                props.onClose();
            });
        }
    };

    const ProductInputFieldsComponent = (
        <ProductInputFields
            productName={productName}
            setProductName={setProductName}
            productType={productType}
            setProductType={setProductType}
            productCategory={productCategory}
            setProductCategory={setProductCategory}
            productDescription={productDescription}
            setProductDescription={setProductDescription}
            productUnit={productUnit}
            setProductUnit={setProductUnit}
            productSku={productSku}
            setProductSku={setProductSku}
            isCreatingCategory={variant === 'create'}
        />
    );

    const ProductMarketFieldArrayComponent = (
        <ProductMarketFieldArray
            marketFields={marketFields}
            handleMarketFieldChange={handleMarketFieldChange}
        />
    );

    return (
        <TwoPaneModalForm
            onSubmit={handleSubmit}
            onClose={props.onClose}
            // @ts-expect-error
            isOpen={props.isOpen}
            submitButtonText={props.isSaving ? 'Saving…' : 'Save product'}
            isSaving={props.isSaving}
            saveButtonDisabled={
                !isValidProduct(productName, productType, productUnit, productDescription)
            }
            saveButtonVariant='save'
            leftSection={<div className='product-input-fields'>{ProductInputFieldsComponent}</div>}
            rightSection={
                <div className='product-market-field-array'>{ProductMarketFieldArrayComponent}</div>
            }
            headerText={props.variant === 'create' ? 'Add a new product' : 'Edit product'}
            headerIcon={<BoxRegularIcon size={24} />}
            maxHeight={700}
            maxWidth={680}
            footerLeftItem={
                product &&
                product.createdTime && (
                    <Body color='light' truncate={{type: 'inline'}} size='small'>
                        Created on {getFooterTimestamp(product.createdTime)}
                    </Body>
                )
            }
        />
    );
}
